import type { SiteResponse } from '@/api/types';

/**
 * Parses published site settings (GET /site) per docs/site-settings-contract.md (version 1).
 * Tolerates missing keys and falls back to neutral defaults — never to prototype sample text
 * or phone numbers.
 */
export type SiteConfig = {
  gates: SiteResponse['gates'];
  transferInstructions: string | null;
  paymentContact: string | null;
  copy: { catalogTitle: string; catalogLead: string; footer: string };
  banner: { on: boolean; text: string };
  whatsapp: { number: string | null; messageTemplate: string | null };
  social: { instagram: string | null; tiktok: string | null };
};

export const NEUTRAL_COPY = {
  catalogTitle: 'Upcoming trips.',
  catalogLead: 'Group trips you can join. Pick one and reserve your spot.',
  footer: 'TripCircle, a travel club.',
};

function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
}

function url(value: unknown): string | null {
  const candidate = text(value, 500);
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export function parseSite(response: SiteResponse): SiteConfig {
  const settings = obj(response.settings);
  const site = obj(settings.site);
  const copy = obj(site.copy);
  const banner = obj(site.banner);
  const whatsapp = obj(site.whatsapp);
  const social = obj(site.social);
  const bannerText = text(banner.text, 200);
  return {
    gates: response.gates,
    transferInstructions: text(settings.transferInstructions, 5000),
    paymentContact: text(settings.paymentContact, 500),
    copy: {
      catalogTitle: text(copy.catalogTitle, 120) ?? NEUTRAL_COPY.catalogTitle,
      catalogLead: text(copy.catalogLead, 400) ?? NEUTRAL_COPY.catalogLead,
      footer: text(copy.footer, 400) ?? NEUTRAL_COPY.footer,
    },
    banner: { on: banner.on === true && bannerText !== null, text: bannerText ?? '' },
    whatsapp: {
      number: text(whatsapp.number, 40),
      messageTemplate: text(whatsapp.messageTemplate, 500),
    },
    social: { instagram: url(social.instagram), tiktok: url(social.tiktok) },
  };
}
